import React from "react";
import { useTheme } from "@emotion/react";
import ReactModal from "react-modal";
import styled from "@emotion/styled";
import { X, Zap } from "lucide-react";
import {
  Checkbox,
  Loader,
  StandardButton,
  SubmitButton,
} from "../../components";
import { COLOURS } from "../../shared";
import { Toggle } from "../toggle";
import {
  isBasicInvoiceOption,
  isDetailedInvoiceOption,
} from "../../shared/utils/invoiceOptionUtils";

const StyledModal = styled.div`
  width: 56rem;
  max-height: 75.7rem;
  border-radius: 0.8rem;
  background-color: ${COLOURS.importAndExportModalContainerBackground};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;

  // Media query for screens below 950px in height
  @media (max-height: 950px) {
    max-height: 65rem;
  }

  // Media query for screens below 850px in height
  @media (max-height: 850px) {
    max-height: 55rem;
  }

  // Media query for screens below 750px in height
  @media (max-height: 750px) {
    max-height: 50rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 7.5rem;
  margin-left: 2.4rem;
  margin-right: 2.4rem;
`;

const IconWrapper = styled.div`
  background-color: ${COLOURS.newMigrationIconWrapperBackground};
  padding: 1.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1.6rem;
  margin-top: 1.6rem;
`;

// Title styling
const Title = styled.h2`
  color: ${COLOURS.importAndExportModalHeadingColor};
  font-size: ${(props) => props.theme.fontSize.subHeading};
  font-weight: 600;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${COLOURS.importAndExportModalHeadingColor};
  font-size: ${(props) => props.theme.fontSize.base};
  font-weight: 500;
  margin-top: 0.4rem;
`;

const CloseButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  margin-left: auto;
`;

// Divider & Other Containers
const Divider = styled.hr`
  border-top: 1px solid ${COLOURS.importAndExportModalDivider};
  margin: 0;
`;

const OptionsContainer = styled.div`
  max-height: 58.8rem;
  width: 56rem;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2.4rem 2.4rem 0.8rem 2.4rem;
`;

const OptionRow = styled.div<{ selected: boolean }>`
  margin-bottom: 0.95rem;
  border: 1px solid ${COLOURS.importAndExportModalDataTypeBorder};
  border-radius: 0.8rem;
  align-items: center;
  justify-content: center;
  padding: 0.7rem;
  user-select: none;
  cursor: pointer;
  background-color: ${({ selected }) =>
    selected
      ? COLOURS.importAndExportModalDataTypeSelectedBackground
      : COLOURS.importAndExportModalContainerBackground};
`;

const Description = styled.p<{
  descriptionIsEmpty?: boolean;
  dependencyIsEmpty?: boolean;
}>`
  color: ${COLOURS.importAndExportModalDataTypeParagraphColor};
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 400;
  line-height: 1.8rem;
  margin: ${(props) =>
    props.descriptionIsEmpty || props.dependencyIsEmpty
      ? "0 0 0 3.6rem"
      : "0 0 0 3.6rem"}; /* Only apply left margin */
  padding: ${(props) =>
    props.descriptionIsEmpty
      ? "0"
      : "0 0 0.2rem 0"}; /* Minimal bottom padding if description exists */
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2.4rem;
  height: 9.2rem;
`;

// Subsection styles
const SubSectionContainer = styled.div`
  border-left: 1px dashed ${COLOURS.importAndExportModalDataTypeBorder};
  margin-left: 3.6rem;
  padding-left: 1.6rem;
  margin-top: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleWrapper = styled.div`
  margin-right: 1.2rem;
`;

const SubSectionLabel = styled.span`
  color: ${COLOURS.importAndExportModalHeadingColor};
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 600;
`;

const SubSectionDescription = styled.p`
  color: ${COLOURS.importAndExportModalDataTypeParagraphColor};
  font-size: ${(props) => props.theme.fontSize.small};
  line-height: 1.6rem;
  margin: 0;
`;
export interface DataTypeOption {
  label: string;
  dataTypes: string[];
  selected: boolean;
  description?: string;
  subSection?: {
    label?: string;
    description?: string[];
    toggleValue?: boolean;
    toggleDisabled?: boolean;
    onToggle?: (value: boolean) => void;
  };
}

interface BaseModalProps {
  titleText: string;
  subtitleText: string;
  closeModal: () => void;
  isLoading: boolean;
  options: DataTypeOption[];
  onToggleOption: (index: number) => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  onProceed: () => void;
}

export const BaseImportExportModalComponent: React.FC<BaseModalProps> = ({
  titleText,
  subtitleText,
  closeModal,
  isLoading,
  options,
  onToggleOption,
  onSelectAll,
  onUnselectAll,
  onProceed,
}) => {
  const theme = useTheme();

  const isBasicOption = (opt: DataTypeOption) =>
    isBasicInvoiceOption(opt.dataTypes);

  const isDetailedOption = (opt: DataTypeOption) =>
    isDetailedInvoiceOption(opt.dataTypes);

  // Interpret ALL SELECTED with mutually exclusive invoice rules
  const allSelected = (() => {
    const basic = options.find(isBasicOption);
    const detailed = options.find(isDetailedOption);

    // all non-invoice options are selected
    const nonInvoiceAllSelected = options
      .filter((o) => !isBasicOption(o) && !isDetailedOption(o))
      .every((o) => o.selected);

    // at least one invoice option is selected (Basic or Detailed, or both)
    const invoiceSelected = !!(basic?.selected || detailed?.selected);

    return nonInvoiceAllSelected && invoiceSelected;
  })();

  const selectedCount = options.filter((o) => o.selected).length;

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={closeModal}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      style={{
        overlay: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.25)",
          padding: 0,
        },
        content: {
          position: "relative",
          borderRadius: "1.2rem",
          margin: "0rem auto",
          padding: 0,
        },
      }}
    >
      <StyledModal>
        {/* Modal Header */}
        <Header>
          <IconWrapper>
            <Zap
              color={COLOURS.newMigrationIconColor}
              strokeWidth={2}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </IconWrapper>
          <TitleContainer>
            <Title>{titleText}</Title>
            <Subtitle>{subtitleText}</Subtitle>
          </TitleContainer>
          <CloseButtonWrapper onClick={closeModal}>
            <X
              size={18}
              strokeWidth={2}
              color={COLOURS.importAndExportModalCloseButton}
            />
          </CloseButtonWrapper>
        </Header>

        {/* Dividing Line Below Heading */}
        <Divider />

        {/* Options List */}
        <OptionsContainer>
          {options.map((option, index) => (
            <OptionRow
              key={index}
              selected={option.selected}
              onClick={() => onToggleOption(index)}
            >
              <Checkbox
                id={`option-${index}`}
                name="dataOption"
                value={option.label}
                checked={option.selected}
                onChange={() => onToggleOption(index)}
                label={option.label}
                index={0}
                type="checkbox"
                labelFontSize={theme.fontSize.base}
                labelColor={COLOURS.importAndExportModalDataTypeHeadingColor}
                shouldStopPropagation={true}
              />
              {option.description && (
                <Description>{option.description}</Description>
              )}
              {option.subSection && (
                <SubSectionContainer>
                  <SubSectionHeader>
                    <SubSectionLabel>{option.subSection.label}</SubSectionLabel>

                    {/* Clickable toggle area with pointer cursor, no bubbling */}
                    <ToggleWrapper
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "pointer" }}
                    >
                      <Toggle
                        toggled={option.subSection.toggleValue}
                        disabled={option.subSection.toggleDisabled}
                        onClick={() =>
                          option.subSection?.onToggle?.(
                            !option.subSection.toggleValue,
                          )
                        }
                      />
                    </ToggleWrapper>
                  </SubSectionHeader>

                  {Array.isArray(option.subSection.description) ? (
                    option.subSection.description.map((line, i) => (
                      <SubSectionDescription key={i}>
                        {line}
                      </SubSectionDescription>
                    ))
                  ) : (
                    <SubSectionDescription>
                      {option.subSection.description}
                    </SubSectionDescription>
                  )}
                </SubSectionContainer>
              )}
            </OptionRow>
          ))}
        </OptionsContainer>

        <Divider />

        <Footer>
          <StandardButton
            onClick={allSelected ? onUnselectAll : onSelectAll}
            fontSize={theme.fontSize.emphasis}
            width="calc(50% - 0.6rem)"
            height="4.4rem"
          >
            {allSelected ? "Unselect All" : "Select All"}
          </StandardButton>
          <SubmitButton
            onClick={onProceed}
            disabled={selectedCount === 0}
            isSubmitButtonDisabled={selectedCount === 0}
            disableButtonAccess={false}
            fontSize={theme.fontSize.emphasis}
            width="calc(50% - 0.6rem)"
            height="4.4rem"
            padding="1.2rem 2.0rem"
          >
            {`${selectedCount} Selected, Proceed`}
          </SubmitButton>
        </Footer>
      </StyledModal>
      {isLoading && <Loader />}
    </ReactModal>
  );
};
